/**
 * CreateFolderDialog - Modal para criar/renomear pastas
 * 
 * Features:
 * - Validação de nome em tempo real
 * - Seleção de cor personalizada
 * - Feedback visual imediato com preview da pasta
 * - Tooltips de ajuda
 */

import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  keyframes,
  useMediaQuery,
  useTheme
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FolderIcon from '@mui/icons-material/Folder'
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder'
import EditIcon from '@mui/icons-material/Edit'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { FOLDER_COLORS } from '../../../types/folder'
import { useFolderColorsFromContext } from '../../../contexts/UIConfigContext'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: (name: string, color: string | null) => void
  /** Se fornecido, abre em modo de edição */
  editMode?: boolean
  initialName?: string
  initialColor?: string | null
  /** Título customizado */
  title?: string
}

export default function CreateFolderDialog({
  open,
  onClose,
  onConfirm,
  editMode = false,
  initialName = '',
  initialColor = null,
  title
}: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  
  // Busca cores da API
  const apiFolderColors = useFolderColorsFromContext()
  const folderColors = apiFolderColors.length > 0 ? apiFolderColors : FOLDER_COLORS

  const [name, setName] = useState(initialName)
  const [color, setColor] = useState<string | null>(initialColor)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setName(initialName)
      setColor(initialColor)
      setError(null)
      // Foca no input após abrir
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open, initialName, initialColor])

  const handleNameChange = (value: string) => {
    setName(value)
    if (value.trim().length === 0) {
      setError('Nome da pasta é obrigatório')
    } else if (value.trim().length > 100) {
      setError('Máximo de 100 caracteres')
    } else {
      setError(null)
    }
  }

  const handleConfirm = () => {
    if (!name.trim()) {
      setError('Nome da pasta é obrigatório')
      return
    }
    onConfirm(name.trim(), color)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !error && name.trim()) {
      handleConfirm()
    }
  }

  const displayTitle = title || (editMode ? 'Renomear pasta' : 'Nova pasta')
  const displayColor = color || '#90CAF9'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          animation: `${fadeIn} 0.2s ease-out`
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }}>
            <Box
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(displayColor, 0.15),
              }}
            >
              {editMode ? (
                <EditIcon sx={{ color: displayColor, fontSize: { xs: 20, sm: 22 } }} />
              ) : (
                <CreateNewFolderIcon sx={{ color: displayColor, fontSize: { xs: 20, sm: 22 } }} />
              )}
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              {displayTitle}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
        <Stack spacing={{ xs: 2.5, sm: 3 }}>
          {/* Preview da pasta */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 2, sm: 3 },
              bgcolor: alpha(displayColor, 0.05),
              borderRadius: 2,
              border: `1px dashed ${alpha(displayColor, 0.3)}`
            }}
          >
            <FolderIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: displayColor, mr: { xs: 1.5, sm: 2 } }} />
            <Typography
              variant="body1"
              fontWeight={600}
              sx={{
                color: name.trim() ? '#333' : '#999',
                fontStyle: name.trim() ? 'normal' : 'italic',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                wordBreak: 'break-word'
              }}
            >
              {name.trim() || 'Nome da pasta...'}
            </Typography>
          </Box>

          {/* Campo de nome */}
          <TextField
            inputRef={inputRef}
            label="Nome da pasta"
            placeholder={isMobile ? 'Ex: Audiências 2026' : 'Ex: Audiências 2026, Depoimentos, etc.'}
            value={name}
            onChange={e => handleNameChange(e.target.value)}
            onKeyDown={handleKeyDown}
            error={!!error}
            helperText={error || `${name.length}/100 caracteres`}
            fullWidth
            autoFocus
            InputProps={{
              sx: { borderRadius: 2 }
            }}
            sx={{
              // ─── Overrides do isolation CSS do WordPress ─────────────────────────────
              // O isolation CSS em main.tsx força:
              //   .MuiOutlinedInput-notchedOutline { border: 1px solid rgba(0,0,0,0.23) !important }
              // com especificidade (1,1,0)+!important, cancelando hover e focus do MUI.
              // Usando '#monkee-file-transcript-client & .Mui...' geramos (1,3,1)+!important.
              '#monkee-file-transcript-client & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.87) !important',
              },
              '#monkee-file-transcript-client & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: '2px solid #1976d2 !important',
              },
              '#monkee-file-transcript-client & .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d32f2f !important',
              },
              '#monkee-file-transcript-client & .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: '2px solid #d32f2f !important',
              },
            }}
          />

          {/* Seleção de cor */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Cor da pasta
              </Typography>
              {!isMobile && (
                <Tooltip title="Escolha uma cor para facilitar a identificação visual da pasta" arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
              )}
            </Stack>
            <Stack direction="row" spacing={{ xs: 0.75, sm: 1 }} flexWrap="wrap" useFlexGap>
              {folderColors.map(fc => (
                <Tooltip key={fc.value} title={isMobile ? '' : fc.label} arrow disableHoverListener={isMobile}>
                  <Box
                    onClick={() => setColor(fc.value)}
                    sx={{
                      width: { xs: 36, sm: 32 },
                      height: { xs: 36, sm: 32 },
                      borderRadius: '50%',
                      bgcolor: fc.value,
                      cursor: 'pointer',
                      border: color === fc.value ? '3px solid #333' : '2px solid transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        transform: 'scale(1.15)',
                        boxShadow: `0 2px 8px ${alpha(fc.value, 0.5)}`
                      }
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: 1,
          '& > button': { width: { xs: '100%', sm: 'auto' } }
        }}
      >
        <Button onClick={onClose} color="inherit" sx={{ minHeight: { xs: 44, sm: 36 } }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!name.trim() || !!error}
          startIcon={editMode ? <EditIcon /> : <CreateNewFolderIcon />}
          sx={{
            px: { xs: 2, sm: 3 },
            minHeight: { xs: 48, sm: 36 },
            bgcolor: displayColor,
            '&:hover': { bgcolor: alpha(displayColor, 0.85) },
            // ─── Overrides do WordPress ──────────────────────────────────────────────
            // Tema WP aplica background-color escuro em button:focus / button:active.
            // '#monkee-file-transcript-client &' gera (1,2,0)+!important — vence o tema WP.
            '#monkee-file-transcript-client &:focus': {
              backgroundColor: `${displayColor} !important`,
              outline: 'none !important',
            },
            '#monkee-file-transcript-client &:focus-visible': {
              backgroundColor: `${displayColor} !important`,
              outline: 'none !important',
            },
            '#monkee-file-transcript-client &:active': {
              backgroundColor: `${alpha(displayColor, 0.85)} !important`,
            },
            '#monkee-file-transcript-client &:hover': {
              backgroundColor: `${alpha(displayColor, 0.85)} !important`,
            },
          }}
        >
          {editMode ? 'Salvar' : (isMobile ? 'Criar' : 'Criar pasta')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
